/**
 * Merkflow Sidebar — nav tree data + filtering.
 *
 * Extracted from Sidebar.tsx. Holds the static nav tree, item/section
 * types, the pure `filterNavItems` visibility helper, and the
 * collapsible-group config. The Sidebar component re-exports
 * `filterNavItems` + `NavItem` for back-compat with consumers (tests).
 */

import {
  LayoutDashboard,
  Inbox,
  Tags,
  BarChart3,
  MessageCircle,
  Megaphone,
  CalendarDays,
  Compass,
  FileText,
  BookOpen,
  Zap,
  Settings,
  Link2,
  Puzzle,
  Clock,
  CreditCard,
  Shield,
  Users as UsersIcon,
  Cpu,
  Mail,
  Bot,
  Timer,
  Activity,
  Building2,
  Layers,
  FlaskConical,
  HardDrive,
  Brain,
  Send,
  Sparkles,
  User as UserIcon,
  Webhook,
  SpellCheck,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { type SidebarGroupKey } from './SidebarGroup';

// ---------------------------------------------------------------------------
// Nav tree
// ---------------------------------------------------------------------------

export type BadgeKey = 'review' | 'engagement' | 'waitlist' | 'feedback';

export interface NavItem {
  to: string;
  label: string;
  /** i18n key — if set, label is resolved via t(i18nKey). Fallback to label. */
  i18nKey?: string;
  icon: LucideIcon;
  badge?: BadgeKey;
  exact?: boolean;
  ownerOnly?: boolean;
  /** Only render when `/api/admin/config.mockEnabled === true` (test env). */
  mockOnly?: boolean;
  /** Hidden from Starter-plan workspaces. Pro / Agency / Trial all
   *  see the full sidebar — Trial gets Pro-level features per
   *  V81+`plan_tier_features` so trialing customers can evaluate
   *  every feature before they hit their billing decision. */
  proOnly?: boolean;
}

/**
 * Pure filter helper for nav items. Exported so tests can pin the
 * Starter-vs-Pro visibility contract without booting the whole
 * Sidebar component.
 *
 * Drop rules:
 *   - `ownerOnly` items dropped when `isOwner === false`
 *   - `mockOnly` items dropped when `mockEnabled === false`
 *   - `proOnly`  items dropped when `isStarter === true`
 */
export function filterNavItems(
  items: NavItem[],
  flags: { isOwner: boolean; mockEnabled: boolean; isStarter: boolean },
): NavItem[] {
  return items.filter(
    (i) =>
      (!i.ownerOnly || flags.isOwner) &&
      (!i.mockOnly || flags.mockEnabled) &&
      (!i.proOnly || !flags.isStarter),
  );
}

export interface NavSection {
  label: string;
  /** i18n key for the section header. If set, label resolved via t(). */
  i18nKey?: string;
  items: NavItem[];
  adminOnly?: boolean;
}

export const NAV: NavSection[] = [
  {
    label: 'Main',
    i18nKey: 'sidebar.sectionMain',
    items: [
      {
        to: '/dashboard',
        label: 'Overview',
        i18nKey: 'nav.overview',
        icon: LayoutDashboard,
        exact: true,
      },
      {
        to: '/dashboard/review',
        label: 'Review',
        i18nKey: 'nav.review',
        icon: Inbox,
        badge: 'review',
      },
      {
        to: '/dashboard/engagement',
        label: 'Engagement',
        i18nKey: 'nav.engagement',
        icon: MessageCircle,
        badge: 'engagement',
      },
    ],
  },
  {
    label: 'Content',
    i18nKey: 'sidebar.sectionContent',
    items: [
      { to: '/dashboard/brands', label: 'Brands', i18nKey: 'nav.brands', icon: Tags },
      { to: '/dashboard/posts', label: 'Posts', i18nKey: 'nav.posts', icon: FileText },
      {
        to: '/dashboard/storylines',
        label: 'Storylines',
        i18nKey: 'nav.storylines',
        icon: BookOpen,
        proOnly: true,
      },
      {
        to: '/dashboard/calendar',
        label: 'Calendar',
        i18nKey: 'nav.calendar',
        icon: CalendarDays,
        proOnly: true,
      },
      {
        to: '/dashboard/discover',
        label: 'Discover',
        i18nKey: 'nav.discover',
        icon: Compass,
        proOnly: true,
      },
      { to: '/dashboard/blog', label: 'Blog', i18nKey: 'nav.blog', icon: FileText, proOnly: true },
    ],
  },
  {
    label: 'Growth',
    i18nKey: 'sidebar.sectionGrowth',
    items: [
      {
        to: '/dashboard/analytics',
        label: 'Analytics',
        i18nKey: 'nav.analytics',
        icon: BarChart3,
        proOnly: true,
      },
      {
        to: '/dashboard/ai-visibility',
        label: 'AI Visibility',
        i18nKey: 'nav.aiVisibility',
        icon: Sparkles,
        proOnly: true,
      },
      { to: '/dashboard/ads', label: 'Ads', i18nKey: 'nav.ads', icon: Megaphone, proOnly: true },
      {
        to: '/dashboard/usage',
        label: 'Usage',
        i18nKey: 'nav.usage',
        icon: Zap,
        ownerOnly: true,
        proOnly: true,
      },
    ],
  },
  {
    label: 'Settings',
    i18nKey: 'sidebar.sectionSettings',
    items: [
      {
        to: '/dashboard/settings/profile',
        label: 'Profile',
        i18nKey: 'nav.profile',
        icon: UserIcon,
      },
      {
        to: '/dashboard/settings',
        label: 'General',
        i18nKey: 'nav.general',
        icon: Settings,
        exact: true,
      },
      {
        to: '/dashboard/settings/members',
        label: 'Members',
        i18nKey: 'nav.members',
        icon: UsersIcon,
        ownerOnly: true,
      },
      {
        to: '/dashboard/settings/connections',
        label: 'Connections',
        i18nKey: 'nav.connections',
        icon: Link2,
        ownerOnly: true,
      },
      {
        to: '/dashboard/settings/connected-apps',
        label: 'Connected Apps',
        i18nKey: 'nav.connectedApps',
        icon: Puzzle,
        ownerOnly: true,
      },
      { to: '/dashboard/settings/cadence', label: 'Cadence', i18nKey: 'nav.cadence', icon: Clock },
      {
        to: '/dashboard/settings/carousel-templates',
        label: 'Carousel templates',
        i18nKey: 'nav.carouselTemplates',
        icon: Layers,
        ownerOnly: true,
        proOnly: true,
      },
      {
        to: '/dashboard/settings/reports',
        label: 'Reports',
        i18nKey: 'nav.reports',
        icon: Mail,
        ownerOnly: true,
        proOnly: true,
      },
      {
        to: '/dashboard/settings/auto-responses',
        label: 'Auto-responses',
        i18nKey: 'nav.autoResponses',
        icon: Bot,
        ownerOnly: true,
        proOnly: true,
      },
      {
        to: '/dashboard/settings/billing',
        label: 'Billing',
        i18nKey: 'nav.billing',
        icon: CreditCard,
        ownerOnly: true,
      },
      { to: '/dashboard/settings/privacy', label: 'Privacy', i18nKey: 'nav.privacy', icon: Shield },
    ],
  },
  {
    label: 'Admin',
    i18nKey: 'sidebar.sectionAdmin',
    adminOnly: true,
    items: [
      { to: '/admin/users', label: 'Users', icon: Shield },
      { to: '/admin/waitlist', label: 'Waitlist', icon: Inbox, badge: 'waitlist' },
      { to: '/admin/feedback', label: 'Feedback', icon: MessageCircle, badge: 'feedback' },
      { to: '/admin/invite-links', label: 'Invite links', icon: Send },
      { to: '/admin/workspaces', label: 'Workspaces', icon: Building2 },
      { to: '/admin/plans', label: 'Plans', icon: Layers },
      { to: '/admin/usage', label: 'Usage', icon: BarChart3 },
      { to: '/admin/ai-settings', label: 'AI Settings', icon: Cpu },
      { to: '/admin/integrations', label: 'Integrations', icon: Link2 },
      { to: '/admin/email', label: 'Email', icon: Mail },
      { to: '/admin/platform-settings', label: 'Platform Settings', icon: Webhook },
      { to: '/admin/style-guardrails', label: 'Style Guardrails', icon: SpellCheck },
      { to: '/admin/generation-traces', label: 'Generation Traces', icon: Workflow },
      { to: '/admin/schedulers', label: 'Schedulers', icon: Timer },
      { to: '/admin/rag', label: 'RAG', icon: Brain },
      // `exact: true` so /admin/platform-settings doesn't prefix-match
      // and light up "Platform" too. Without it, NavLink's default
      // partial match flags both entries active when the user is on
      // Platform Settings.
      { to: '/admin/platform', label: 'Platform', icon: Activity, exact: true },
      { to: '/admin/s3', label: 'S3 Browser', icon: HardDrive },
      // Test env only — gated by /api/admin/config.mockEnabled (see
      // visibleItems below). Hidden on prod regardless of admin role.
      { to: '/admin/mock-platform', label: 'Mock Platform', icon: FlaskConical, mockOnly: true },
    ],
  },
];

/**
 * Sections that render inside a collapsible `SidebarGroup`. Keyed by the
 * NavSection `label` (not i18n key, since that's resolved at render time).
 * Anything not listed here stays flat with a plain `SectionHeader`.
 *
 * `storageKey` — field in the `merkflow-sidebar-groups` localStorage blob.
 * `routePrefix` — pathname match that auto-opens the group on mount /
 *   navigation (so hitting /admin/plans via URL reveals Admin even if
 *   the user had folded it away).
 */
export const COLLAPSIBLE_GROUPS: Record<
  string,
  { storageKey: SidebarGroupKey; routePrefix: string }
> = {
  Settings: { storageKey: 'settings', routePrefix: '/dashboard/settings' },
  Admin: { storageKey: 'admin', routePrefix: '/admin' },
};
